import type { BlockPhase, MusclePriorities, VolumeTargets } from '@cadencia/contracts';
import {
  bandFor,
  buildPhaseBlock,
  EXERCISES,
  catalogForBlockProposal,
  exerciseAllowed,
  MUSCLES,
  PHASE_RAMPS,
  PLANNED_WEEKS_BY_PHASE,
  priorityFor,
  SAFETY_LIMITS,
  validateBlockProposal,
  validateSessionPlan,
  evidenceCaveat,
  evidenceForPlanning,
  memoryForPlanning,
  resolveCitationRefs,
  usableSources,
  weeklyVolumeByMuscle,
  BLOCK_WEEKS_SLACK,
  type BlockProposalContext,
  type CoachAIProvider,
  type Muscle,
  type ResolvedCitation,
} from '@cadencia/domain';
import type { SessionPlan } from '@cadencia/contracts';
import { logger } from '../../lib/logger.js';
import { getDefaultGym, listEquipment } from '../repositories/gymRepo.js';
import { getProfile } from '../repositories/profileRepo.js';
import { estadoDeLaEvidencia } from './evidenceService.js';
import { athleteMemory } from './outcomeService.js';
import { motivoDeFalloIA } from './motivoIA.js';

/**
 * Propuesta de bloque con IA (§7 y §9 del encargo).
 *
 * QUE SE LE PREGUNTA, DESDE EL 22/09/2026
 *
 * Las SESIONES del bloque —cuantas, como se llaman, que entrena cada una, con
 * que ejercicios y en que rol—, ademas de las semanas y las series por musculo
 * que ya se le preguntaban.
 *
 * Antes eran solo esas dos cifras, dentro de un bloque cuya estructura ya
 * estaba decidida por una plantilla escrita a mano. El §7 del encargo señala
 * exactamente ese problema: no se puede afirmar que la IA diseña la
 * programacion mientras las plantillas lo hacen imposible.
 *
 * NO se le pregunta el split como etiqueta aparte: el split ES como se agrupan
 * las sesiones, y preguntarlo ademas abriria la puerta a que la etiqueta y las
 * sesiones se contradigan. Tampoco la fase, que la decide `selectBlockPhase`
 * con las señales declaradas del atleta, ni la rampa entre semanas, que es
 * progresion y la ejecuta el motor local (§7: «el motor local ejecuta las
 * reglas de progresion y ajuste inmediato del plan validado»).
 *
 * LA BASE Y EL CATALOGO VIAJAN CON LA PREGUNTA
 *
 * El contexto lleva, por musculo, las series que produce la plantilla: sin esa
 * cifra «sube el volumen del dorsal» no significa nada, y ademas es la
 * referencia del tope del ±20 %. Y lleva los ejercicios que CABEN en el
 * gimnasio del usuario, porque el §7 exige que elija entre ejercicios validos
 * del catalogo y no invente identificadores ni equipamiento.
 *
 * DOS VEREDICTOS CON DOS REGLAS DISTINTAS
 *
 * Los numeros se RECORTAN (banda del musculo, ±20 %); la estructura se RECHAZA
 * ENTERA. El motivo esta en `session-plan.ts` y es el mismo que ya gobernaba
 * este fichero: una lista de numeros independientes se puede arreglar pieza a
 * pieza, un conjunto no.
 *
 * Esto no crea ningun bloque. Devuelve una propuesta ya validada por el
 * dominio, y quien decida aplicarla es el usuario desde `Propuestas`.
 */

export interface PropuestaDeBloque {
  weeks: number;
  targets: VolumeTargets;
  /** Lo que escribio el modelo. Ya paso por `validateAIProse`. */
  rationale: string;
  /** Lo que el dominio recorto, y respecto a que. Se enseña. */
  adjustments: readonly string[];
  /** Entradas descartadas enteras: un musculo que no existe, uno repetido. */
  dropped: readonly string[];
  /** Semanas que la fase tiene tabuladas, para poder decir de que se aparta. */
  defaultWeeks: number;
  /**
   * Las sesiones que diseño la IA, ya validadas, o `null` si se rechazaron.
   *
   * `null` NO significa «la IA no contesto»: significa que contesto y su
   * estructura no pasa las reglas del dominio. Quien lo aplique usa la
   * plantilla determinista, y `planRejectedFor` dice por que.
   */
  sessionPlan: SessionPlan | null;
  /** Motivos del rechazo de la estructura. Vacio si se acepto. */
  planRejectedFor: readonly string[];
  /**
   * Con que version de la evidencia se hizo esta propuesta (§12).
   *
   * El §12 exige registrar «version de evidencia» en cada propuesta de IA, y
   * esto es esa version: la fecha de la ultima revision de literatura y las
   * fichas activas que habia en ese momento. Sirve para que dentro de tres
   * meses se pueda saber sobre que se decidio, y para que una fuente
   * retractada despues deje rastro en los planes que la tuvieron delante.
   *
   * `reviewedOn` es `null` cuando nunca se ha revisado nada, que es el estado
   * inicial y NO es lo mismo que una base vencida.
   */
  evidence: {
    reviewedOn: string | null;
    /** Identificadores de las fichas activas. Vacio si no hay ninguna. */
    activeSourceIds: readonly string[];
    /** La limitacion que hay que decir junto al plan. `null` si no la hay. */
    caveat: string | null;
  };
  /**
   * Las fichas que la IA cito, ya traducidas y resueltas contra la base (§14).
   *
   * Se guarda el identificador y la etiqueta, no la referencia «E1»: la
   * referencia solo significaba algo dentro de la peticion que la numero.
   */
  citations: readonly ResolvedCitation[];
  /** Citas que no resolvieron, con el motivo. Se registran, no se esconden. */
  citationsRejected: readonly { ref: string; reason: string }[];
  /** Cuantas observaciones de la memoria del atleta viajaron con la pregunta (E5). */
  memoryFindings: number;
}

function musculosPrioritarios(priorities: MusclePriorities | undefined): string[] {
  if (priorities === undefined) return [];
  return MUSCLES.filter((muscle) => priorityFor(muscle as Muscle, priorities) === 'high');
}

/**
 * La propuesta y, si no la hay, POR QUÉ (24/09/2026).
 *
 * `proposeBlock` devolvía `null` para cualquier fallo —IA apagada, sin
 * presupuesto, sin red, respuesta inválida— y el bloque salía de la plantilla
 * sin que nadie supiera cuál de ellos había sido. `motivo` es `null` si hay
 * propuesta.
 */
export interface ResultadoDePropuesta {
  propuesta: PropuestaDeBloque | null;
  motivo: string | null;
}

export async function proposeBlock(
  ia: CoachAIProvider,
  userId: string,
  phase: BlockPhase,
  options: { musclePriorities?: MusclePriorities; phaseReasonCodes?: readonly string[] } = {},
): Promise<PropuestaDeBloque | null> {
  return (await proponerBloque(ia, userId, phase, options)).propuesta;
}

export async function proponerBloque(
  ia: CoachAIProvider,
  userId: string,
  phase: BlockPhase,
  options: { musclePriorities?: MusclePriorities; phaseReasonCodes?: readonly string[] } = {},
): Promise<ResultadoDePropuesta> {
  if (!ia.enabled) return { propuesta: null, motivo: 'la IA está desactivada' };

  const gym = await getDefaultGym(userId);
  const equipment = gym ? await listEquipment(gym.id) : [];
  const kinds = [...new Set(equipment.map((item) => item.kind))];
  // Lo que el atleta declaró en el test inicial (docs/TEST_INICIAL.md §2.3).
  const perfil = await getProfile(userId);
  const availability = perfil?.availability ?? null;
  const limitations = perfil?.limitations ?? [];

  // La referencia: lo que saldria sin preguntar nada. Se construye entero y se
  // mira solo la primera semana, que es la que fija el punto de partida.
  const determinista = buildPhaseBlock(phase, {
    availableEquipmentKinds: kinds,
    musclePriorities: options.musclePriorities,
    availability,
    limitations,
  });
  const primeraSemana = determinista.sessions.filter((sesion) => sesion.weekIndex === 1);
  const base = weeklyVolumeByMuscle(primeraSemana);

  const defaultWeeks = PLANNED_WEEKS_BY_PHASE[phase];
  const baseline: Partial<Record<Muscle, number>> = {};
  for (const [muscle, sets] of base) baseline[muscle as Muscle] = Math.round(sets);

  const musculos = MUSCLES.filter((muscle) => (baseline[muscle as Muscle] ?? 0) > 0).map(
    (muscle) => {
      const band = bandFor(muscle as Muscle);
      return {
        muscle,
        templateSets: baseline[muscle as Muscle] ?? 0,
        bandMin: band.min,
        bandTargetMin: band.targetMin,
        bandTargetMax: band.targetMax,
        bandMax: band.max,
      };
    },
  );

  /*
   * El catalogo que se le ofrece: los ejercicios que CABEN en este gimnasio.
   *
   * El filtro por equipamiento se hace aqui y no se delega al modelo con una
   * lista de maquinas, por dos motivos. Uno, que un ejercicio que no se puede
   * hacer no es una opcion y ofrecerlo solo invita a que lo elija. Dos, que el
   * contexto tiene un tope (`MAX_CONTEXT_TOKENS`) y el catalogo entero no es
   * gratis: filtrar antes es lo que lo deja caber.
   *
   * `bodyweight` siempre esta, igual que en `buildBlock`.
   */
  const disponibles = new Set([...kinds, 'bodyweight' as const]);
  // Lo que las limitaciones vetan tampoco se ofrece, por el mismo motivo que
  // el equipamiento: ofrecerlo solo invita a elegirlo. Y así no hace falta
  // contarle a la IA qué articulación le molesta al atleta. La biblioteca v2
  // no cabe en el contexto: `catalogForBlockProposal` se queda con el
  // catálogo original (medido allí).
  const catalogo = catalogForBlockProposal(
    EXERCISES.filter(
      (ejercicio) =>
        disponibles.has(ejercicio.equipmentKind) &&
        exerciseAllowed(ejercicio, limitations).allowed,
    ),
  );

  /*
   * LA VERSION DE EVIDENCIA, que el §12 exige registrar con cada propuesta.
   *
   * Se lee ANTES de preguntar, no despues: lo que hay que registrar es con que
   * se decidio, y entre la pregunta y la respuesta pueden pasar segundos en los
   * que otra pestaña active una ficha. Y ahora ademas VIAJA con la pregunta,
   * numerada, para que la IA pueda citarla (§14).
   *
   * NO BLOQUEA NADA aunque este vencida o vacia (§8). Sin fichas, `evidence`
   * va vacio y la IA programa con las reglas generales, que es lo que se dice
   * junto al plan.
   */
  const evidencia = await estadoDeLaEvidencia(userId);
  const activas = usableSources(evidencia.sources);
  const paraPlanificar = evidenceForPlanning(evidencia.sources);

  // Lo aprendido de este atleta (E5). Derivado ahora, no leido de una fila:
  // no puede ir mas viejo que los datos.
  const memoria = memoryForPlanning(await athleteMemory(userId));

  const contexto: BlockProposalContext = {
    phase,
    defaultWeeks,
    minWeeks: Math.max(1, defaultWeeks - BLOCK_WEEKS_SLACK.shorter),
    maxWeeks: defaultWeeks + BLOCK_WEEKS_SLACK.longer,
    phaseReasonCodes: options.phaseReasonCodes ?? [],
    priorityMuscles: musculosPrioritarios(options.musclePriorities),
    maxSessionsPerWeek: SAFETY_LIMITS.session.maxStrengthSessionsPerWeek,
    sessionsPerWeek: availability?.days.length ?? null,
    // El tiempo que tiene el atleta, si lo declaró; nunca más que el de la fase.
    maxSessionMinutes: Math.min(
      PHASE_RAMPS[phase].maxSessionMinutes,
      availability?.maxSessionMinutes ?? PHASE_RAMPS[phase].maxSessionMinutes,
    ),
    catalog: catalogo,
    muscles: musculos,
    athleteMemory: memoria,
    evidence: paraPlanificar.context,
  };

  const resultado = await ia.proposeBlock(contexto);
  if (!resultado.ok) {
    return { propuesta: null, motivo: motivoDeFalloIA(resultado.error, 'proponer el bloque') };
  }

  const veredicto = validateBlockProposal({
    defaultWeeks,
    baseline,
    proposal: {
      weeks: resultado.value.weeks,
      volumeTargets: resultado.value.volumeTargets,
    },
  });

  /*
   * La ESTRUCTURA se juzga aparte de los numeros, y con otra regla: los
   * numeros se recortan, la estructura se rechaza entera. El motivo esta en
   * `session-plan.ts`: una semana de entrenamiento es un conjunto, y quedarse
   * con las sesiones que estan bien da un plan que no ha diseñado nadie.
   *
   * Un plan rechazado NO tumba la propuesta: las semanas y el volumen siguen
   * valiendo, y el bloque se construye con la plantilla determinista. Es la
   * respuesta degradada del §13, y se distingue de «la IA no contesto» porque
   * `planRechazado` trae los motivos.
   */
  const plan = validateSessionPlan({
    proposal: resultado.value.sessions,
    availableEquipmentKinds: kinds,
    expectedSessions: availability?.days.length,
    limitations,
    // El catálogo de ESTA petición, no el global (encargo del 23/09/2026, §3).
    allowedSlugs: new Set(Object.values(catalogo).flat()),
  });

  if (plan.sessions === null) {
    logger.warn('ai', 'BLOCK_PLAN_REJECTED', 'Plan de sesiones rechazado por el dominio', {
      phase,
      motivos: plan.motivos,
    });
  }

  const citas = resolveCitationRefs(
    resultado.value.citations,
    paraPlanificar.refs,
    evidencia.sources,
  );
  if (citas.rejected.length > 0) {
    logger.warn('ai', 'BLOCK_CITATIONS_REJECTED', 'Citas de la propuesta que no resolvieron', {
      phase,
      rechazadas: citas.rejected,
    });
  }

  if (veredicto.adjustments.length > 0 || veredicto.dropped.length > 0) {
    // Se registra siempre que el dominio haya tenido que intervenir. No es un
    // error —recortar es su trabajo— pero cuantas veces pasa dice algo sobre el
    // prompt que no se puede saber de otra forma.
    logger.warn('ai', 'BLOCK_PROPOSAL_ADJUSTED', 'Propuesta de bloque recortada por el dominio', {
      phase,
      adjustments: veredicto.adjustments,
      dropped: veredicto.dropped,
    });
  }

  /*
   * El veredicto FINAL, ahora que el dominio ha juzgado volumen y estructura.
   * El filtro de prosa del proveedor la dejó pendiente a propósito: pasar la
   * prosa no es aceptar. Aceptada = la estructura propuesta vale (con o sin
   * recortes de volumen, que se dicen); rechazada = se usa la plantilla, y el
   * motivo lo explica. Aplicarla —crear el bloque— es otro paso.
   */
  if (resultado.decisionId !== undefined) {
    const recortes = [...veredicto.adjustments, ...veredicto.dropped];
    ia.recordVerdict?.(resultado.decisionId, {
      accepted: plan.sessions !== null,
      reason:
        plan.sessions === null
          ? `estructura rechazada: ${plan.motivos.join('; ')}`.slice(0, 300)
          : recortes.length > 0
            ? `aceptada con recortes: ${recortes.join('; ')}`.slice(0, 300)
            : null,
    });
  }

  const propuesta: PropuestaDeBloque = {
    weeks: veredicto.weeks,
    targets: veredicto.targets,
    rationale: resultado.value.rationale,
    adjustments: veredicto.adjustments,
    dropped: veredicto.dropped,
    defaultWeeks,
    sessionPlan: plan.sessions as SessionPlan | null,
    planRejectedFor: plan.motivos,
    evidence: {
      reviewedOn: evidencia.freshness.lastReviewedOn,
      activeSourceIds: activas.map((fila) => fila.id),
      caveat: evidenceCaveat(evidencia.freshness),
    },
    citations: citas.resolved,
    citationsRejected: citas.rejected,
    memoryFindings: memoria.length,
  };
  return { propuesta, motivo: null };
}
